<script lang="ts" setup generic="T extends string | Component">
import {
  computed,
  useCssModule,
  useSlots,
  h,
  mergeProps,
  useAttrs,
  type Component,
  type VNode,
} from 'vue';
import { IconClose } from '@/icons';
import type { PolymorphicProps } from '../../shared/types';

interface Props extends PolymorphicProps<T> {
  filled?: boolean;
  removable?: boolean;
  active?: boolean;
}

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<Props>();

const emit = defineEmits<{
  remove: [];
}>();

defineSlots<{
  default?: () => VNode;
  meta?: () => VNode;
  leadingVisual?: () => VNode;
  counter?: () => VNode;
}>();

const slots = useSlots();
const attrs = useAttrs();
const style = useCssModule();

const rootProps = computed(() => {
  return {
    class: [
      style.root,
      {
        [style.filled]: props.filled,
        [style.removable]: props.removable,
        [style.active]: props.active,
      },
    ],
    'data-ui-component': !props.removable ? 'UiChip' : undefined,
  };
});

defineRender(() => {
  const text = slots.default?.();
  const meta = slots.meta?.();
  const counter = slots.counter?.();
  const leadingVisual = slots.leadingVisual?.();

  // Have to use such complicated structure to support clickable and removable at the same time

  const internalStructure = [
    h('span', { class: style.content }, [
      leadingVisual ? h('span', { class: style.leadingVisual }, leadingVisual) : null,
      text ? h('span', { class: style.text }, text) : null,
    ]),
    counter ? h('span', { class: style.counter }, counter) : null,
    meta ? h('span', { class: style.meta }, meta) : null,
  ];

  const content = props.as
    ? h(props.as, mergeProps(attrs, rootProps.value, props.asProps ?? {}), internalStructure)
    : h('span', rootProps.value, internalStructure);

  return props.removable
    ? h('span', { class: style.wrapper, 'data-ui-component': 'UiChip' }, [
        content,
        h(
          'button',
          {
            type: 'button',
            class: style.remove,
            onclick: () => {
              emit('remove');
            },
          },
          h(IconClose, { ariaHidden: true }),
        ),
      ])
    : content;
});
</script>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/iconography';
@use '../../styles/shared';
@use '../../styles/typography';

.wrapper {
  display: inline-flex;
  position: relative;

  &:hover {
    .remove {
      opacity: 1;
    }

    a.root,
    button.root {
      background: rgba(0, 0, 0, 0.04);
    }
  }
}

.root {
  position: relative;
  font-family: typography.$font-family-primary;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  box-sizing: border-box;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  height: 24px;
  border-radius: 16px;
  border: 1px solid #d5d6d9;
  background: transparent;
  color: #010000;
  padding: 0 (8px - 1px); // border

  &.active {
    background: rgba(0, 0, 0, 0.08);
    color: #226b6c;
  }

  &.filled {
    color: #226b6c;
    background: rgba(34, 107, 108, 0.12);
    border: none;
    padding: 0 8px;

    &.active {
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
        rgba(34, 107, 108, 0.12);
    }
  }
}

a.root,
button.root {
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }

  .wrapper:hover &,
  &:hover {
    background: rgba(0, 0, 0, 0.04);

    &.filled {
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.04) 100%),
        rgba(34, 107, 108, 0.12);
    }
  }

  &:active {
    background: rgba(0, 0, 0, 0.08);
    color: #226b6c;

    &.filled {
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
        rgba(34, 107, 108, 0.12);
    }
  }
}

.content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.leadingVisual {
  width: 18px;
  height: 18px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > img,
  > svg {
    max-width: 100%;
    max-height: 100%;
  }
}

.counter {
  font-family: typography.$font-family-monospace;
  color: #f5f5f5;
  background-color: #226b6c;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 14px;
  padding: 0 4px;
  box-sizing: border-box;
}

.text {
  max-width: 220px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.meta {
  color: #655e60;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  max-width: 120px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.remove {
  display: flex;
  width: 18px;
  height: 18px;
  padding: 2px;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 10;
  top: 50%;
  transform: translateY(-50%);
  right: 2px;
  background-color: #fff;
  color: #010000;
  border-radius: 100%;
  opacity: 0;
  border: none;
  cursor: pointer;

  > svg {
    width: 100%;
    height: 100%;
  }

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
    opacity: 1;
  }
}
</style>
